import { useState, useEffect } from 'react';
import { aboutRepository } from '../../../../domain/repositories';

/**
 * Custom hook to fetch only members data
 * @returns {Object} Object containing members, loading state, and error
 */
export const useMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const membersData = await aboutRepository.getMembersByRole();
        setMembers(membersData);
      } catch (err) {
        console.error('Error fetching members:', err);
        setError(err.message || 'Failed to load members');
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, []);

  return { members, loading, error };
};

/**
 * Custom hook to fetch only photos data with pagination
 * @returns {Object} Object containing visible photos, loading state, error, and loadMore function
 */
export const usePhotos = () => {
  const [allPhotos, setAllPhotos] = useState([]);
  const [visibleCount, setVisibleCount] = useState(9);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const photosData = await aboutRepository.getAllPhotos();
        setAllPhotos(photosData);
      } catch (err) {
        console.error('Error fetching photos:', err);
        setError(err.message || 'Failed to load photos');
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  const loadMore = () => {
    setVisibleCount(prev => Math.min(prev + 9, allPhotos.length));
  };

  const visiblePhotos = allPhotos.slice(0, visibleCount);
  const hasMore = visibleCount < allPhotos.length;

  return { 
    photos: visiblePhotos, 
    loading, 
    error, 
    loadMore,
    hasMore
  };
};
